import { useCallback, useLayoutEffect, useMemo } from 'react';

import { useSelect } from 'downshift';

import { mergeRefs, useForwardedRef } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import useGroupedItems from './useGroupedItems';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  identityFn,
  isGroupHeader,
  wrapGetItemProps,
} from '../helpers';
import {
  DefaultItem,
  GenericItem,
  GroupHeader,
  InputItemGroup,
  UseDropdownProps,
} from '../types';

/**
 * A hook used to manage all logic needed for a Dropdown.
 */
const useDropdown = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement | HTMLLabelElement,
>({
  itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
  items,
  groupedItems,
  isItemDisabled = defaultIsItemDisabled,
  target,
  layoutDependency,
  getMenuProps: getMenuPropsProp,
  ...props
}: UseDropdownProps<TItem, TTarget>) => {
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality({ ...props, items: maybeGroupedItems });

  const targetRef = useForwardedRef(target || null);
  const { flatItems, indexGetter, getMenuItems } = useGroupedItems({
    maybeGroupedItems,
  });

  const {
    isOpen: downshiftIsMenuOpen,
    getItemProps: rawGetItemProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    ...useSelectReturnValue
  } = useSelect({
    itemToString,
    items: flatItems,
    ...props,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
    ...useOverlayReturnValue
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    target: targetRef,
    layoutDependency,
    isOverlayConditionallyRendered: false,
    ...props,
  });

  const getMenuProps: typeof rawGetMenuProps = useCallback(
    (args) =>
      rawGetMenuProps(
        (getMenuPropsProp || identityFn)({ ...overlayProps, ...args }),
      ),
    [getMenuPropsProp, overlayProps, rawGetMenuProps],
  );

  type MenuItems = typeof groupedItems extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];

  const menuItems = useMemo(
    () => getMenuItems(flatItems) as MenuItems,
    [flatItems, getMenuItems],
  );

  const defaultIndexGetter = useCallback(
    (item: TItem) => menuItems.indexOf(item),
    [menuItems],
  );

  const getItemProps: typeof rawGetItemProps = useCallback(
    (args) =>
      wrapGetItemProps({
        getItemProps: rawGetItemProps,
        isItemDisabled,
        highlightedIndex: useSelectReturnValue.highlightedIndex,
        selectedItem: useSelectReturnValue.selectedItem,
        indexGetter: indexGetter || defaultIndexGetter,
      })(args),
    [
      defaultIndexGetter,
      indexGetter,
      isItemDisabled,
      rawGetItemProps,
      useSelectReturnValue.highlightedIndex,
      useSelectReturnValue.selectedItem,
    ],
  );

  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);

  return {
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    isOpen: useOverlayIsOpen,
    /**
     * @deprecated use value returned from menuProps
     */
    menuItems,
    /**
     * @deprecated use value returned from menuProps
     */
    getItemProps,
    /**
     * @deprecated use value returned from menuProps
     */
    getMenuProps,
    controlProps: {
      ref: targetRef,
      isOpen: useOverlayIsOpen,
      getToggleButtonProps: useSelectReturnValue.getToggleButtonProps,
      openMenu: useSelectReturnValue.openMenu,
      selectedItem: useSelectReturnValue.selectedItem,
      itemToString,
      selectItem: selectItem as (item: TItem | null) => void,
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex: useSelectReturnValue.highlightedIndex,
      selectedItem: useSelectReturnValue.selectedItem,
      width: targetRef.current?.offsetWidth,
      selectItem,
      isGroupHeader,
    },
    selectItem,
    ...useSelectReturnValue,
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    targetProps: { ref: targetRef },
    ...useOverlayReturnValue,
  };
};

export default Object.assign(useDropdown, {
  stateChangeTypes: useSelect.stateChangeTypes,
});
